import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import b2bActivateLogo from "@/assets/b2b-activate-logo.png";

/**
 * Resolves the issuer logo URL for a corporate entity.
 * Resolution chain: corporate -> parent corporate -> B2B Activate fallback.
 */
export function useIssuerLogo(corporateId: number) {
  const { data: logoUrl, isLoading } = useQuery({
    queryKey: ["issuer-logo", corporateId],
    queryFn: async () => {
      // 1. Check the corporate's own logo_url
      const { data: corp } = await supabase
        .from("corporates")
        .select("logo_url, parent_corporate_id")
        .eq("id", corporateId)
        .single();

      if (corp?.logo_url) return corp.logo_url;

      // 2. Walk up to parent corporate
      if (corp?.parent_corporate_id) {
        const { data: parent } = await supabase
          .from("corporates")
          .select("logo_url")
          .eq("id", corp.parent_corporate_id)
          .single();

        if (parent?.logo_url) return parent.logo_url;
      }

      // 3. Fallback to default asset
      return b2bActivateLogo;
    },
    enabled: corporateId > 0,
    staleTime: 5 * 60 * 1000, // cache 5 min
  });

  return { logoUrl: logoUrl ?? b2bActivateLogo, isLoading };
}

/**
 * Resolves the branded task domain for a corporate entity.
 * Resolution chain: corporate -> parent corporate -> fallback domain.
 */
export async function resolveTaskDomain(corporateId: number): Promise<string> {
  const { data: corp } = await supabase
    .from("corporates")
    .select("custom_task_domain, parent_corporate_id")
    .eq("id", corporateId)
    .single() as { data: { custom_task_domain: string | null; parent_corporate_id: number | null } | null };

  if (corp?.custom_task_domain) return corp.custom_task_domain.replace(/\/$/, "");

  if (corp?.parent_corporate_id) {
    const { data: parent } = await supabase
      .from("corporates")
      .select("custom_task_domain")
      .eq("id", corp.parent_corporate_id)
      .single() as { data: { custom_task_domain: string | null } | null };

    if (parent?.custom_task_domain) return parent.custom_task_domain.replace(/\/$/, "");
  }

  return "https://app.b2bactivate.com";
}

/**
 * Fetches the logo_url for a corporate (non-hook version for use in async handlers).
 */
export async function resolveLogoUrl(corporateId: number): Promise<string | null> {
  const { data: corp } = await supabase
    .from("corporates")
    .select("logo_url, parent_corporate_id")
    .eq("id", corporateId)
    .single();

  if (corp?.logo_url) return corp.logo_url;

  if (corp?.parent_corporate_id) {
    const { data: parent } = await supabase
      .from("corporates")
      .select("logo_url")
      .eq("id", corp.parent_corporate_id)
      .single();

    if (parent?.logo_url) return parent.logo_url;
  }

  return null;
}
